import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DashboardRequestError } from "../providers/aws/client";
import { QueueRows } from "./QueueRows";
import { QueueRunDialog } from "./QueueRunDialog";
import { ReadyToRender } from "./ReadyToRender";
import { label, timestamp } from "./format";
import { groupRunsByPhase } from "./runPhase";
import type { QueueOverview } from "./types";
import { useQueueResource } from "./useQueueResource";

export function QueuePage() {
  const [search, setSearch] = useSearchParams();
  const [cursors, setCursors] = useState<string[]>([]);
  const [staleCursors, setStaleCursors] = useState<string[]>([]);
  const [staleOpen, setStaleOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const product = search.get("product_flow") ?? "";
  const channel = search.get("channel") ?? "";
  const attention = search.get("attention") ?? "";
  const [selected, setSelected] = useState<string | null>(() =>
    search.get("echo"),
  );
  const params = new URLSearchParams();
  if (product) params.set("product_flow", product);
  if (channel) params.set("channel", channel);
  if (attention) params.set("attention", attention);
  if (cursors.length) params.set("cursor", cursors[cursors.length - 1]);
  if (staleCursors.length)
    params.set("stale_cursor", staleCursors[staleCursors.length - 1]);
  const resource = useQueueResource<QueueOverview>(
    `/pipeline/queue?${params}`,
    "list",
  );
  useEffect(() => {
    if (
      resource.error instanceof DashboardRequestError &&
      resource.error.code === "cursor_expired"
    ) {
      setCursors([]);
      setStaleCursors([]);
      setNotice("The queue changed. Pagination restarted from the first page.");
    }
  }, [resource.error]);
  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(search);
    if (value) next.set(key, value);
    else next.delete(key);
    setCursors([]);
    setStaleCursors([]);
    setSearch(next);
  };
  const data = resource.data;
  // The render gate's own table, polled at the Queue's list cadence; shown
  // even when the pipeline monitor is unavailable.
  const readyToRender = (
    <ReadyToRender
      refreshSeconds={data?.display_policy.list_refresh_seconds ?? 15}
      onSelect={setSelected}
    />
  );
  return (
    <main className="mx-auto w-full max-w-7xl space-y-6 p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Queue</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every admitted Echo, from intake to delivery.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={resource.reload}
          disabled={resource.loading}
        >
          <RefreshCw className="mr-2 size-4" />
          Refresh
        </Button>
      </div>
      <div className="flex flex-wrap gap-3">
        <Filter
          label="Product"
          value={product}
          onChange={(value) => setFilter("product_flow", value)}
          options={[
            ["", "All products"],
            ["b2c", "B2C"],
            ["real_estate", "Real estate"],
            ["unknown", "Unknown"],
          ]}
        />
        <Filter
          label="Destination"
          value={channel}
          onChange={(value) => setFilter("channel", value)}
          options={[
            ["", "All destinations"],
            ["whatsapp", "WhatsApp"],
            ["studio", "Studio"],
            ["remake", "Remake"],
            ["internal", "Internal"],
          ]}
        />
        <Filter
          label="Attention"
          value={attention}
          onChange={(value) => setFilter("attention", value)}
          options={[
            ["", "All active Echoes"],
            ["authority_conflict", "Conflicting evidence"],
            ["outcome_unknown", "Outcome unknown"],
            ["admission_unconfirmed", "Admission unconfirmed"],
            ["progress_overdue", "Progress overdue"],
          ]}
        />
      </div>
      {notice && (
        <p role="status" className="text-sm">
          {notice}
        </p>
      )}
      {resource.error && (
        <div
          role="status"
          className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-sm"
        >
          {data
            ? "Live updates are unavailable. The last known queue remains below."
            : "Queue monitoring is unavailable. Refresh to try again."}
        </div>
      )}
      {!data && resource.loading && <p role="status">Loading queue…</p>}
      {!data && resource.error && readyToRender}
      {data && (
        <>
          <p className="text-xs text-muted-foreground">
            {data.environment === "stg"
              ? "Production (stg)"
              : "Engineering staging (staging)"}{" "}
            · Fetched {timestamp(data.as_of)} · Projection{" "}
            {data.projection_generation}
          </p>
          {!data.coverage.discovery_complete && (
            <div
              role="status"
              className="rounded-lg border border-amber-500/40 p-3 text-sm"
            >
              Monitoring is still catching up on{" "}
              {catchingUp(data.coverage.sources)}. Recently admitted Echoes may
              appear shortly.
            </div>
          )}
          {data.coverage.discovery_complete &&
            data.coverage.status !== "ready" && (
              <p
                role="status"
                className="rounded-lg border border-amber-500/40 p-3 text-sm"
              >
                Some monitoring checks were unavailable on the last pass, so a
                few stage updates or delivery readbacks may be delayed. Nothing
                here is missing on purpose.
              </p>
            )}
          {data.coverage.observer && (
            <details className="rounded-lg border p-3 text-sm">
              <summary className="cursor-pointer">
                Monitor transport: {data.coverage.observer.status}
              </summary>
              <p className="mt-2 text-xs text-muted-foreground">
                These are approximate counts of monitoring events, separate from
                the Echo list.
              </p>
              {Object.entries(data.coverage.observer.queues ?? {}).map(
                ([name, queue]) => (
                  <p key={name} className="mt-1">
                    {name === "dead_letter"
                      ? "Dead-letter events"
                      : "Pending observations"}
                    :{" "}
                    {queue.status === "ready"
                      ? `${queue.visible ?? 0} waiting, ${queue.in_flight ?? 0} being processed, ${queue.delayed ?? 0} delayed`
                      : "Unavailable"}
                  </p>
                ),
              )}
            </details>
          )}
          {readyToRender}
          <section aria-label="Active Echoes">
            <h2 className="font-semibold">In queue</h2>
            <p className="mb-3 mt-1 text-xs text-muted-foreground">
              Grouped by the phase each Echo is in, furthest along first; most
              recent activity first within a phase.
            </p>
            {data.active.items.length ? (
              <div className="space-y-5">
                {groupRunsByPhase(data.active.items).map((group) => (
                  <div key={group.phase.key}>
                    <h3 className="mb-2 text-sm font-semibold">
                      {group.phase.label}{" "}
                      <span className="font-normal text-muted-foreground">
                        ({group.items.length})
                      </span>
                    </h3>
                    <QueueRows
                      items={group.items}
                      asOf={data.as_of}
                      onSelect={setSelected}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p className="rounded-lg border p-6 text-muted-foreground">
                {data.active.next_cursor
                  ? "No matches on this page. Continue to check later admissions."
                  : data.coverage.discovery_complete
                    ? "No active Echoes match these filters."
                    : "No Echoes observed on this page; discovery is incomplete."}
              </p>
            )}
            <div className="mt-3 flex gap-2">
              {cursors.length > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setCursors((current) => current.slice(0, -1))}
                >
                  Previous page
                </Button>
              )}
              {data.active.next_cursor && (
                <Button
                  variant="outline"
                  onClick={() =>
                    setCursors((current) => [
                      ...current,
                      data.active.next_cursor!,
                    ])
                  }
                >
                  Next page
                </Button>
              )}
            </div>
          </section>
          {data.stale && (
            <section aria-label="Stale Echoes" className="border-t pt-6">
              <details
                open={staleOpen}
                onToggle={(event) =>
                  setStaleOpen((event.target as HTMLDetailsElement).open)
                }
              >
                <summary className="cursor-pointer font-semibold">
                  Stale Echoes ({data.stale.unfiltered_total})
                </summary>
                <p className="mb-3 mt-1 text-xs text-muted-foreground">
                  Admitted more than {staleAge(data.stale_after_seconds)} ago
                  without any exit evidence. Kept out of the queue above so it
                  shows current work; newest first.
                </p>
                {data.stale.items.length ? (
                  <QueueRows
                    items={data.stale.items}
                    asOf={data.as_of}
                    onSelect={setSelected}
                  />
                ) : (
                  <p className="rounded-lg border p-6 text-muted-foreground">
                    {data.stale.next_cursor
                      ? "No matches on this page. Continue to older admissions."
                      : "No stale Echoes match these filters."}
                  </p>
                )}
                <div className="mt-3 flex gap-2">
                  {staleCursors.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={() =>
                        setStaleCursors((current) => current.slice(0, -1))
                      }
                    >
                      Previous stale page
                    </Button>
                  )}
                  {data.stale.next_cursor && (
                    <Button
                      variant="outline"
                      onClick={() =>
                        setStaleCursors((current) => [
                          ...current,
                          data.stale!.next_cursor!,
                        ])
                      }
                    >
                      Next stale page
                    </Button>
                  )}
                </div>
              </details>
            </section>
          )}
          <section aria-label="Recent queue exits" className="border-t pt-6">
            <h2 className="font-semibold">Last 5 Echoes to leave the queue</h2>
            <p className="mb-3 mt-1 text-xs text-muted-foreground">
              Includes completed, failed and cancelled Echoes for the selected
              product and destination.
            </p>
            {data.recent_exits.length ? (
              <QueueRows
                items={data.recent_exits}
                asOf={data.as_of}
                onSelect={setSelected}
              />
            ) : (
              <p className="rounded-lg border p-6 text-muted-foreground">
                No recent exits recorded.
              </p>
            )}
            {!data.recent_exits_complete && (
              <p className="mt-2 text-xs text-muted-foreground">
                Recent-exit coverage is partial.
              </p>
            )}
          </section>
        </>
      )}
      {selected && (
        <QueueRunDialog
          key={selected}
          workload={selected}
          onClose={() => {
            setSelected(null);
            // Restore after React removes the modal's focus trap and portal.
            requestAnimationFrame(() =>
              document
                .querySelector<HTMLElement>(
                  `[data-queue-workload="${CSS.escape(selected)}"]`,
                )
                ?.focus(),
            );
          }}
        />
      )}
    </main>
  );
}
QueuePage.path = "/queue";

function catchingUp(sources: Array<{ name: string; status: string }>): string {
  const names = sources
    .filter((source) => source.status !== "ready")
    .map((source) => label(source.name.replace(/\.v\d+$/, "")));
  return names.length ? names.join(", ") : "its sources";
}

function staleAge(seconds: number | undefined) {
  const hours = Math.round((seconds ?? 86400) / 3600);
  if (hours >= 24 && hours % 24 === 0) {
    const days = hours / 24;
    return `${days} day${days === 1 ? "" : "s"}`;
  }
  return `${hours} h`;
}

function Filter({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[][];
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-muted-foreground">
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 rounded-md border bg-background px-3 text-sm text-foreground"
      >
        {options.map(([key, title]) => (
          <option key={key} value={key}>
            {title}
          </option>
        ))}
      </select>
    </label>
  );
}
